// src/pages/FeatureHomeDesktop.jsx
import React, { useMemo, useState } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderDesktop from "../_components/_global/HeaderDesktop";
import "bootstrap-icons/font/bootstrap-icons.css";

// ---- small helper: scoped id for CSS isolation ----
function useScopeId(prefix = "fhd") {
    return useMemo(() => `${prefix}-${Math.random().toString(36).slice(2, 8)}`, [prefix]);
}

// ---- demo city meta（你可替换为真实数据）----
const CITY_META = {
    toronto: { name: "Toronto", country: "Canada", temp: 22, cond: "Partly Cloudy", humidity: 58, wind: "11km/h", vis: "10km" },
    vancouver: { name: "Vancouver", country: "Canada", temp: 18, cond: "Cloudy", humidity: 63, wind: "9km/h", vis: "9km" },
    montreal: { name: "Montreal", country: "Canada", temp: 20, cond: "Sunny", humidity: 52, wind: "10km/h", vis: "11km" },
    shanghai: { name: "Shanghai", country: "China", temp: 22, cond: "Partly Cloudy", humidity: 65, wind: "12km/h", vis: "8km" },
};

// ---- demo places（可接后端分页/筛选）----
const DEMO_PLACES = [
    // Canada
    {
        id: 1, name: "CN Tower", type: "Observation Deck",
        distance: "1.5 km", duration: "1–2 hours",
        tags: ["Skyline", "Glass Floor"], rating: 4.7, price: "CA$43–53", icon: "bi-building",
        image: "https://images.unsplash.com/photo-1517935706615-2717063c2225?q=80&w=1600&auto=format&fit=crop"
    },
    {
        id: 2, name: "Stanley Park", type: "Urban Park",
        distance: "3.2 km", duration: "2–3 hours",
        tags: ["Seawall", "Totem Poles"], rating: 4.8, price: "Free", icon: "bi-tree",
        image: "https://i0.wp.com/vancouversbestplaces.com/wp-content/uploads/2020/08/Stanley-Park-Lighthouse-by-On-the-Mark-Drone-Services.jpg?w=640&ssl=1"
    },
    {
        id: 3, name: "Old Montreal", type: "Historic District",
        distance: "2.1 km", duration: "2–3 hours",
        tags: ["Cobblestone", "Notre-Dame"], rating: 4.7, price: "Free", icon: "bi-bank2",
        image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSLz00hcDlkCi1ohtuqDc2RmiYzNLrp1TBTKQ&s"
    },
    {
        id: 4, name: "Niagara Falls", type: "Natural Wonder",
        distance: "130 km", duration: "Half day",
        tags: ["Boat Tour", "Viewpoints"], rating: 4.9, price: "Free–CA$35", icon: "bi-water",
        image: "https://images.unsplash.com/photo-1502082553048-f009c37129b9?q=80&w=1600&auto=format&fit=crop"
    },
    {
        id: 5, name: "Capilano Suspension Bridge", type: "Attraction",
        distance: "8.0 km", duration: "1–2 hours",
        tags: ["Cliffwalk", "Rainforest"], rating: 4.6, price: "CA$66", icon: "bi-bridge",
        image: "https://images.unsplash.com/photo-1504196606672-aef5c9cefc92?q=80&w=1600&auto=format&fit=crop"
    },
    {
        id: 6, name: "Banff National Park", type: "National Park",
        distance: "125 km", duration: "Day trip",
        tags: ["Lake Louise", "Hiking"], rating: 4.9, price: "CA$10–22", icon: "bi-signpost-2",
        image: "https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=1600&auto=format&fit=crop"
    },

    // USA
    {
        id: 7, name: "Times Square", type: "Landmark",
        distance: "0.5 km", duration: "1–2 hours",
        tags: ["Broadway", "Neon"], rating: 4.5, price: "Free", icon: "bi-brightness-high",
        image: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?q=80&w=1600&auto=format&fit=crop"
    },
    {
        id: 8, name: "Central Park", type: "Urban Park",
        distance: "2.7 km", duration: "2–4 hours",
        tags: ["Lakes", "Bow Bridge"], rating: 4.8, price: "Free", icon: "bi-tree",
        image: "https://d2wsrtli9cxkek.cloudfront.net/media/images/Sheep-Meadow-Aerial-Central-Park-credit-the-Central-Park-Conservancy.jpg?auto=compress%2Cformat&crop=focalpoint&fit=crop&fp-x=0.5&fp-y=0.5&h=1151.1627906977&q=80&w=2475&s=d3c4f805891782ac9d112b33cc8d3f03"
    },
    {
        id: 9, name: "The Getty Center", type: "Art Museum",
        distance: "14.0 km", duration: "2–3 hours",
        tags: ["Architecture", "Gardens"], rating: 4.7, price: "Free", icon: "bi-building",
        image: "https://www.tripsavvy.com/thmb/0o-QzB2Tnhd4lLLFblZGwDmZIzM=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/Los-Angeles-Assignment-1-60WEB-5c741eedc9e77c00016bfdfd.jpg"
    },
    {
        id: 10, name: "Santa Monica Pier", type: "Beach & Pier",
        distance: "20.0 km", duration: "2–3 hours",
        tags: ["Sunset", "Rides"], rating: 4.6, price: "Free", icon: "bi-sunset",
        image: "https://images.unsplash.com/photo-1509718443690-d8e2fb3474b7?q=80&w=1600&auto=format&fit=crop"
    },
    {
        id: 11, name: "Art Institute of Chicago", type: "Art Museum",
        distance: "1.2 km", duration: "2–3 hours",
        tags: ["Impressionism", "Gilded Lions"], rating: 4.8, price: "US$25–32", icon: "bi-palette",
        image: "https://dynamic-media-cdn.tripadvisor.com/media/photo-o/0f/a4/dd/83/photo0jpg.jpg?w=900&h=500&s=1"
    },

    // Mexico (North America)
    {
        id: 12, name: "Frida Kahlo Museum (Casa Azul)", type: "Museum",
        distance: "10.0 km", duration: "1–2 hours",
        tags: ["Coyoacán", "Art"], rating: 4.6, price: "MX$250–300", icon: "bi-brush",
        image: "https://images.unsplash.com/photo-1541944743827-e04aa6427c33?q=80&w=1600&auto=format&fit=crop"
    },
];


export default function FeatureHomeDesktop() {
    const scope = useScopeId();
    const navigate = useNavigate();
    const { state } = useLocation();
    const cityKey = (state?.selectedCity || "toronto").toLowerCase();
    const city = CITY_META[cityKey] || CITY_META.toronto;

    const [activeTab, setActiveTab] = useState("All");
    const [view, setView] = useState("grid"); // grid | list

    const css = `
  [data-fhd="${scope}"] .grid-gap{gap:16px}
  [data-fhd="${scope}"] .card-soft{border:1px solid #e8edf2;border-radius:18px;background:#fff}
  [data-fhd="${scope}"] .btn-ghost{background:#fff;border:1px solid #e6eaf0;border-radius:12px;font-weight:700}
  [data-fhd="${scope}"] .btn-darkish{background:#0f2538;border:none;border-radius:12px;color:#fff;font-weight:700}
  [data-fhd="${scope}"] .chip{padding:8px 12px;border-radius:999px;border:1px solid #e7edf5;background:#f6f8fb;font-weight:600;font-size:13px;color:#0f1720;cursor:pointer}
  [data-fhd="${scope}"] .chip.active{background:#0f1720;color:#fff;border-color:#0f1720}
  [data-fhd="${scope}"] .toolbar{display:flex;align-items:center;gap:8px}
  [data-fhd="${scope}"] .section-h{display:flex;align-items:center;justify-content:space-between;margin:10px 0 14px}
  [data-fhd="${scope}"] .section-h .left h4{margin:0;font-weight:800;color:#0f1720}
  [data-fhd="${scope}"] .section-h .left .sub{color:#6a7a8a;margin-top:2px}
  [data-fhd="${scope}"] .tool-btn{width:100%;padding:16px;border:1px solid #e8edf2;border-radius:14px;background:#fff;text-align:center}
  [data-fhd="${scope}"] .tool-btn i{font-size:18px;margin-bottom:6px;color:#0f2538}
  [data-fhd="${scope}"] .feature-card{padding:18px}
  [data-fhd="${scope}"] .feature-card .btn-darkish{margin-top:auto}
  [data-fhd="${scope}"] .feature-card{display:flex;flex-direction:column}
  [data-fhd="${scope}"] .toolbar{flex-wrap:wrap;gap:8px}
  [data-fhd="${scope}"] .feature-icon{width:40px;height:40px;border-radius:10px;display:flex;align-items:center;justify-content:center;margin-bottom:10px;color:#fff;font-size:18px}
  [data-fhd="${scope}"] .feature-title{font-size:18px;font-weight:700;margin:0 0 6px;color:#0f1720}
  [data-fhd="${scope}"] .feature-desc{color:#6a7a8a;margin:0 0 12px}
 /* === Elegant Weather Card === */
/* === Weather (equal height) === */
[data-fhd="${scope}"] .weather2{
  position:relative;
  padding:24px;
  border-radius:20px;
  color:#eaf3fb;
  background:
    radial-gradient(1200px 600px at -10% -40%, rgba(255,255,255,.08), transparent 40%),
    linear-gradient(135deg,#1f2a44 0%, #2b4e6b 55%, #365e80 100%);
  border:1px solid rgba(255,255,255,.08);
  box-shadow:0 18px 40px rgba(2,6,23,.28);
  overflow:hidden;

  /* ✅ 关键：让卡片自身“能被拉伸”并内部自适应 */
  height:100%;
  display:flex;
  flex-direction:column;
}

[data-fhd="${scope}"] .weather2:after{
  content:""; position:absolute; inset:0; pointer-events:none;
  background: radial-gradient(600px 200px at 110% -10%, rgba(255,255,255,.12), transparent 40%);
}

[data-fhd="${scope}"] .wx-top{
  display:flex; align-items:flex-start; justify-content:space-between; gap:16px;
  /* 顶部内容保持自然高度 */
  flex:0 0 auto;
}

[data-fhd="${scope}"] .wx-city{ font-size:20px; font-weight:800; letter-spacing:.2px; }
[data-fhd="${scope}"] .wx-date{ opacity:.85; margin-top:2px; }

[data-fhd="${scope}"] .wx-temp{ font-size:54px; line-height:1; font-weight:800; letter-spacing:-1px; }
[data-fhd="${scope}"] .wx-cond{ font-size:15px; opacity:.95; margin-top:6px; }
[data-fhd="${scope}"] .wx-hi-lo{ font-size:13px; opacity:.85; }

[data-fhd="${scope}"] .wx-chips{ display:flex; flex-wrap:wrap; gap:8px; margin-top:14px; }
[data-fhd="${scope}"] .wx-chip{
  display:inline-flex; align-items:center; gap:6px;
  padding:8px 10px; border-radius:999px;
  background:rgba(255,255,255,.10); border:1px solid rgba(255,255,255,.16);
  backdrop-filter: blur(6px);
  font-size:13px; color:#eaf3fb;
}
[data-fhd="${scope}"] .wx-chip i{ opacity:.95; }

[data-fhd="${scope}"] .wx-right{ text-align:right; }
[data-fhd="${scope}"] .wx-right .wx-temp{ font-size:42px; }

/* ✅ 小时预报固定在卡片底部，撑满剩余空间 */
[data-fhd="${scope}"] .wx-hours{
  display:flex; gap:10px;
  margin-top:auto;              /* 关键：把小时预报推到最下方 */
  padding-top:8px; padding-bottom:4px;
  overflow:auto;
  border-top:1px solid rgba(255,255,255,.14);
}
[data-fhd="${scope}"] .wx-hour{
  min-width:88px; text-align:center; padding:10px 8px;
  background:rgba(255,255,255,.10); border:1px solid rgba(255,255,255,.16);
  border-radius:14px; backdrop-filter:blur(6px); color:#eaf3fb;
}
[data-fhd="${scope}"] .wx-hour .t{ font-size:18px; font-weight:700; }
[data-fhd="${scope}"] .wx-hour .ti{ font-size:12px; opacity:.9; }
[data-fhd="${scope}"] .wx-hour i{ font-size:18px; opacity:.95; margin:6px 0 4px; }

@media (max-width: 1280px){
  [data-fhd="${scope}"] .wx-temp{ font-size:48px; }
}
  /* place cards */
  [data-fhd="${scope}"] .place-card{border:1px solid #e8edf2;border-radius:18px;overflow:hidden;background:#fff;position:relative}
  [data-fhd="${scope}"] .place-thumb{height:160px;background:#f4f6f9;display:flex;align-items:center;justify-content:center;font-size:36px;color:#8ba0b4}
  [data-fhd="${scope}"] .place-body{padding:14px 16px}
  [data-fhd="${scope}"] .place-title{font-weight:700;color:#0f1720;margin:0 0 2px}
  [data-fhd="${scope}"] .place-sub{color:#6a7a8a;margin-bottom:10px}
  [data-fhd="${scope}"] .meta{display:flex;gap:16px;color:#6a7a8a;font-size:13px;margin-bottom:10px}
  [data-fhd="${scope}"] .tags{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:10px}
  [data-fhd="${scope}"] .tag{background:#eef2f7;border-radius:999px;padding:6px 10px;font-size:12px;color:#334354}
  [data-fhd="${scope}"] .foot{display:flex;align-items:center;justify-content:space-between;color:#6a7a8a}

  /* image support */
[data-fhd="${scope}"] .place-thumb{
  height:160px; overflow:hidden; background:#f4f6f9; /* 原样保持 */
  border-bottom:1px solid #eef2f6;
}
[data-fhd="${scope}"] .place-img{
  width:100%; height:100%; object-fit:cover; display:block;
}
/* list 视图下，左侧图定宽，仍然铺满容器 */
[data-fhd="${scope}"] .place-card.list .place-thumb{ width:260px; height:auto; }
[data-fhd="${scope}"] .place-card.list .place-img{ width:100%; height:100%; object-fit:cover; }
[data-fhd="${scope}"] .place-card.list{ display:flex; gap:16px; }
  `;

    return (
        <>
            <style>{css}</style>

            {/* 顶部导航（作用域样式，不会被覆盖） */}
            <HeaderDesktop
                active="home"
                cityName={`${city.name}`}
                unread={3}
                onSearch={(q) => navigate(`/explore?q=${encodeURIComponent(q)}&city=${cityKey}`)}
            />


            <Container fluid="xxl" data-fhd={scope} className="py-3">
                {/* ===== Top row: Weather + Quick Tools ===== */}
                <Row className="g-3 align-items-start">
                    <Col lg={8}>
                        <div className="weather2 compact">
                            {/* 顶部：城市/日期 + 温度/状态 */}
                            <div className="wx-top">
                                <div>
                                    <div className="wx-city">
                                        <i className="bi bi-geo-alt me-1" />
                                        {city.name} <span className="text-light-emphasis" style={{ fontWeight: 500 }}> {city.country}</span>
                                    </div>
                                    <div className="wx-date">
                                        {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                                    </div>

                                    <div className="wx-temp mt-2">{city.temp}°</div>
                                    <div className="wx-cond">{city.cond}</div>
                                    <div className="wx-hi-lo">Feels like {city.temp}° • H {city.temp + 3}° / L {city.temp - 2}°</div>

                                    {/* 指标 Chips */}
                                    <div className="wx-chips">
                                        <div className="wx-chip"><i className="bi bi-wind" /> {city.wind || "10km/h"}</div>
                                        <div className="wx-chip"><i className="bi bi-moisture" /> Humidity {city.humidity ?? 58}%</div>
                                        <div className="wx-chip"><i className="bi bi-sun" /> UV {city.uv ?? 4}</div>
                                        <div className="wx-chip"><i className="bi bi-eye" /> Visibility {city.vis || "10km"}</div>
                                    </div>
                                </div>

                                {/* 右侧：当前温度/状态（对齐） */}
                                <div className="wx-right">
                                    <div className="wx-temp">{city.temp}°</div>
                                    <div className="wx-cond">{city.cond}</div>
                                </div>
                            </div>

                            {/* 小时预报（示例数据，可接真实 API） */}
                            {/* <div className="wx-hours">
                                {[
                                    { ti: "Now", icon: "bi-sun", t: city.temp },
                                    { ti: "13:00", icon: "bi-cloud-sun", t: city.temp + 1 },
                                    { ti: "14:00", icon: "bi-cloud-sun", t: city.temp + 2 },
                                    { ti: "15:00", icon: "bi-cloud-drizzle", t: city.temp + 1 },
                                    { ti: "16:00", icon: "bi-cloud", t: city.temp },
                                    { ti: "17:00", icon: "bi-cloud-sun", t: city.temp + 1 },
                                ].map(h => (
                                    <div key={h.ti} className="wx-hour">
                                        <div className="ti">{h.ti}</div>
                                        <i className={`bi ${h.icon}`} />
                                        <div className="t">{h.t}°</div>
                                    </div>
                                ))}
                            </div> */}
                        </div>
                    </Col>

                    <Col lg={4}>
                        <Card className="card-soft p-3 ">
                            <div className="fw-bold mb-2">Quick Tools</div>
                            <Row className="g-2">
                                {[
                                    { icon: "bi-send", label: "Navigation" },
                                    { icon: "bi-cash-coin", label: "Currency" },
                                    { icon: "bi-translate", label: "Translate" },
                                    { icon: "bi-camera", label: "Photo ID" },
                                ].map(t => (
                                    <Col xs={6} key={t.label}>
                                        <button className="tool-btn">
                                            <i className={`bi ${t.icon}`} /><div>{t.label}</div>
                                        </button>
                                    </Col>
                                ))}
                            </Row>
                        </Card>
                    </Col>
                </Row>

                {/* ===== Feature cards ===== */}
                <Row className="g-3 mt-3">
                    {[
                        { color: "#2563eb", icon: "bi-journal-bookmark", title: "Smart Trip Planner", desc: "AI-powered itinerary creation", to: "/chat" },
                        { color: "#16a34a", icon: "bi-send", title: "AI City Guide", desc: "Real-time navigation & insights", to: "/chat" },
                        { color: "#7c3aed", icon: "bi-people", title: "Travel Community", desc: "Connect with fellow travelers", to: "/community" },
                    ].map(f => (
                        <Col lg={4} key={f.title}>
                            <Card className="card-soft feature-card h-100 ">
                                <div className="feature-icon" style={{ background: f.color }}>
                                    <i className={`bi ${f.icon}`} />
                                </div>
                                <div className="feature-title">{f.title}</div>
                                <div className="feature-desc">{f.desc}</div>
                                <Button className="btn-darkish" onClick={() => navigate(f.to)}>
                                    Get Started <i className="bi bi-chevron-right ms-1" />
                                </Button>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* ===== Discover section ===== */}
                <div className="section-h mt-4">
                    <div className="left">
                        <h4>Discover {city.name}</h4>
                        <div className="sub">Top-rated places and experiences</div>
                    </div>
                    <div className="toolbar">
                        {[
                            { key: "All", label: "All (124)" },
                            { key: "Attractions", label: "Attractions (45)" },
                            { key: "Restaurants", label: "Restaurants (38)" },
                            { key: "Hotels", label: "Hotels (22)" },
                            { key: "Activities", label: "Activities (19)" },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                className={`chip ${activeTab === tab.key ? "active" : ""}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                        <button className="chip"><i className="bi bi-funnel me-1" /> Filter</button>
                        <button className="chip"><i className="bi bi-sort-down me-1" /> Sort</button>
                        <button className="chip" onClick={() => setView("grid")} title="Grid">
                            <i className="bi bi-grid-3x3-gap" />
                        </button>
                        <button className="chip" onClick={() => setView("list")} title="List">
                            <i className="bi bi-list" />
                        </button>
                    </div>
                </div>

                {/* ===== Places grid/list ===== */}
                <Row className="g-3 align-items-stretch">
                    {DEMO_PLACES.map(p => (
                        <Col key={p.id} lg={view === "grid" ? 4 : 12} md={view === "grid" ? 6 : 12}>
                            <div className={`place-card h-100 ${view === "list" ? "d-flex" : ""}`}>
                                <div className="place-thumb" style={view === "list" ? { width: 260 } : undefined}>
                                    {p.image ? (
                                        <img className="place-img" src={p.image} alt={p.name} loading="lazy" />
                                    ) : (
                                        <i className={`bi ${p.icon}`} />
                                    )}
                                </div>
                                <div className="place-body w-100">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <div className="place-title">{p.name}</div>
                                            <div className="place-sub">{p.type}</div>
                                        </div>
                                        <div className="text-muted">
                                            <i className="bi bi-heart me-2" />
                                            <i className="bi bi-share" />
                                        </div>
                                    </div>

                                    <div className="meta">
                                        <span><i className="bi bi-geo-alt" /> {p.distance}</span>
                                        <span><i className="bi bi-alarm" /> {p.duration}</span>
                                    </div>

                                    <div className="tags">
                                        {p.tags.map(t => <span key={t} className="tag">{t}</span>)}
                                    </div>

                                    <div className="foot">
                                        <div className="text-warning"><i className="bi bi-star-fill" /> {p.rating.toFixed(1)} <span className="text-muted">(1,234)</span></div>
                                        <div>{p.price}</div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
            </Container>
        </>
    );
}
